import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import NoAccess from './pages/NoAccess';
import Dashboard from './pages/Dashboard';
import Kennisbank from './pages/Kennisbank';
import EntryDetail from './pages/EntryDetail';
import EntryForm from './pages/EntryForm';
import Settings from './pages/Settings';
import ComplianceDashboard from './pages/ComplianceDashboard';
import Logboek from './pages/Logboek';
import DocumentUpload from './pages/DocumentUpload';
import DocumentReview from './pages/DocumentReview';
import AiInzichten from './pages/AiInzichten';
import AiNormalisatie from './pages/AiNormalisatie';
import LocationPage from './pages/LocationPage';
import Contracten from './pages/Contracten';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, hasAccess, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-pulse text-muted-foreground">Laden...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/geen-toegang" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-pulse text-muted-foreground">Laden...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/geen-toegang" element={<NoAccess />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppShell>
            <Dashboard />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/kennisbank" element={
        <ProtectedRoute>
          <AppShell>
            <Kennisbank />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/kennisbank/nieuw" element={
        <ProtectedRoute>
          <AppShell>
            <EntryForm />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/kennisbank/upload" element={
        <ProtectedRoute>
          <AppShell>
            <DocumentUpload />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/kennisbank/document/:uploadId" element={
        <ProtectedRoute>
          <AppShell>
            <DocumentReview />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/kennisbank/:entryId" element={
        <ProtectedRoute>
          <AppShell>
            <EntryDetail />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/kennisbank/:entryId/bewerken" element={
        <ProtectedRoute>
          <AppShell>
            <EntryForm />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/compliance" element={
        <ProtectedRoute>
          <AppShell>
            <ComplianceDashboard />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/contracten" element={
        <ProtectedRoute>
          <AppShell>
            <Contracten />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/logboek" element={
        <ProtectedRoute>
          <AppShell>
            <Logboek />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/instellingen" element={
        <ProtectedRoute>
          <AppShell>
            <Settings />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/inzichten" element={
        <ProtectedRoute>
          <AppShell>
            <AiInzichten />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/ai-normalisatie" element={
        <ProtectedRoute>
          <AppShell>
            <AiNormalisatie />
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="/locatie/:locationId" element={
        <ProtectedRoute>
          <AppShell>
            <LocationPage />
          </AppShell>
        </ProtectedRoute>
      } />
      {/* Bestanden folded into Kennisbank as a tab — keep the route as a redirect
          so existing bookmarks / deep links / post-upload redirects still resolve. */}
      <Route path="/bestanden" element={<Navigate to="/kennisbank?tab=bestanden" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
